import React, { useEffect, useState } from 'react';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import List from '@mui/material/List';
import { Link, useLocation } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import ListItemIcon from '@mui/material/ListItemIcon';
import Collapse from '@mui/material/Collapse';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Tooltip from '@mui/material/Tooltip';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BalanceIcon from '@mui/icons-material/Balance';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import GroupIcon from '@mui/icons-material/Group';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import Badge from '@mui/material/Badge';
import { PoliceIcon } from 'components/Shared/SvgIcons';
import { primary, secondary } from 'helpers/colors';
import {
  getUnseenImprovementsCount,
  isImprovementsFirstAccess
} from 'components/Pages/Admin/ImprovementsPath/constants';
import { improvementsBadgeEmitter } from 'components/Pages/Admin/event/eventEmitter';
import { IMPROVEMENTS_BADGE_DISMISS } from 'helpers/constants';
import { ROLE, User } from 'services/auth';

import {
  CustomList,
  CustomListItem,
  ListIconStyle,
  IconStyle,
  CustomListItemText,
  TextDecorationNone,
  CustomBox,
  CustomIconButton
} from './styles';

const expandedDrawerWidth = 240;
const collapsedDrawerWidth = 72;

function Sidebar() {
  const [isExpanded, setIsExpanded] = useState(true);
  const [adminOpen, setAdminOpen] = useState(false);
  const { pathname } = useLocation();
  const unseenImprovements = getUnseenImprovementsCount();
  const [showImprovements, setShowImprovements] = useState<boolean>(() => isImprovementsFirstAccess());

  const toggleDrawer = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    const handleDismiss = () => setShowImprovements(false);

    improvementsBadgeEmitter.on(IMPROVEMENTS_BADGE_DISMISS, handleDismiss);

    return () => {
      improvementsBadgeEmitter.off(IMPROVEMENTS_BADGE_DISMISS, handleDismiss);
    };
  }, []);

  const userSerialized = localStorage.getItem('user') || '';
  const currentUser: User = userSerialized ? JSON.parse(userSerialized) : {};
  const userRole = userSerialized ? currentUser.roles[0].name : '';

  const isUPTeam =
    currentUser?.access_groups &&
    currentUser.access_groups.some((group: any) => group.name && group.name.toLowerCase() === 'uplogteam');

  const logisticsAndFinance = [
    {
      id: 3,
      name: 'Logística',
      icon: <LocalShippingIcon style={IconStyle} />,
      url: '/dashboard/logistica'
    },
    {
      id: 4,
      name: 'Financeiro',
      icon: <MonetizationOnIcon style={IconStyle} />,
      url: '/dashboard/financeiro'
    }
  ];

  const legalAndAdmin = [
    {
      id: 5,
      name: 'Jurídico',
      icon: <BalanceIcon style={IconStyle} />,
      url: '/dashboard/juridico'
    },
    {
      id: 2,
      name: 'Multas',
      icon: <PoliceIcon style={IconStyle} />,
      url: '/multas'
    },
    {
      id: 8,
      name: 'Relatórios',
      icon: <AssessmentIcon style={IconStyle} />,
      url: '/relatorios'
    }
  ];

  const adminMenuItems = [
    {
      id: 6,
      name: 'Novo Usuário',
      icon: <GroupAddIcon style={IconStyle} />,
      url: '/usuarios/novo'
    },
    ...(isUPTeam
      ? [
          {
            id: 7,
            name: 'Grupos de Acesso',
            icon: <GroupIcon style={IconStyle} />,
            url: '/grupos-acesso'
          }
        ]
      : [])
  ];

  return (
    <>
      <CustomIconButton
        onClick={toggleDrawer}
        style={{
          left: isExpanded ? expandedDrawerWidth - 22 : collapsedDrawerWidth - 22
        }}
      >
        {isExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
      </CustomIconButton>
      <Drawer
        variant='permanent'
        sx={{
          width: isExpanded ? expandedDrawerWidth : collapsedDrawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isExpanded ? expandedDrawerWidth : collapsedDrawerWidth,
            boxSizing: 'border-box',
            background: primary.main,
            border: '1px solid rgb(161 161 161 / 30%)',
            borderRadius: 0,
            transition: 'width 0.3s'
          }
        }}
      >
        <Toolbar style={{ minHeight: 60, display: 'flex', justifyContent: 'flex-end' }} />
        <CustomBox>
          {logisticsAndFinance.length > 0 && (
            <CustomList>
              {logisticsAndFinance.map(item => (
                <Link key={item.id} to={item.url} style={TextDecorationNone}>
                  <Tooltip title={item.name} placement='right'>
                    <CustomListItem $active={pathname.startsWith(item.url)}>
                      <ListItemIcon style={ListIconStyle}>{item.icon}</ListItemIcon>
                      {isExpanded && <CustomListItemText primary={item.name} />}
                    </CustomListItem>
                  </Tooltip>
                </Link>
              ))}
            </CustomList>
          )}
          {legalAndAdmin.length > 0 && (
            <CustomList style={{ position: 'relative', bottom: 30 }}>
              {legalAndAdmin.map(item => (
                <Link key={item.id} to={item.url} style={TextDecorationNone}>
                  <Tooltip title={item.name} placement='right'>
                    <CustomListItem $active={pathname.startsWith(item.url)}>
                      <ListItemIcon style={ListIconStyle}>{item.icon}</ListItemIcon>
                      {isExpanded && <CustomListItemText primary={item.name} />}
                    </CustomListItem>
                  </Tooltip>
                </Link>
              ))}
            </CustomList>
          )}
          {showImprovements && (
            <CustomList style={{ position: 'relative', bottom: 60 }}>
              <Link to='/melhorias' style={TextDecorationNone}>
                <Tooltip title='Novidades do sistema' placement='right'>
                  <CustomListItem $active={pathname.startsWith('/melhorias')}>
                    <ListItemIcon style={ListIconStyle}>
                      <Badge color='secondary' badgeContent={unseenImprovements} max={9} overlap='circular'>
                        <AutoAwesomeOutlinedIcon style={IconStyle} />
                      </Badge>
                    </ListItemIcon>
                    {isExpanded && (
                      <>
                        <CustomListItemText primary='Novidades' />
                        <span
                          style={{
                            background: secondary.main,
                            color: secondary.contrast,
                            fontSize: 10,
                            fontWeight: 700,
                            borderRadius: 10,
                            padding: '2px 8px',
                            marginRight: 8
                          }}
                        >
                          NOVO
                        </span>
                      </>
                    )}
                  </CustomListItem>
                </Tooltip>
              </Link>
            </CustomList>
          )}
          {userRole === ROLE.ADMIN && (
            <CustomList style={{ position: 'relative', bottom: showImprovements ? 90 : 60 }}>
              <CustomListItem
                as='button'
                onClick={() => {
                  if (!isExpanded) {
                    setIsExpanded(true);
                    setAdminOpen(true);
                  } else {
                    setAdminOpen(!adminOpen);
                  }
                }}
                style={{ width: '100%', background: 'none', border: 'none' }}
              >
                <ListItemIcon style={ListIconStyle}>
                  <AdminPanelSettingsIcon style={IconStyle} />
                </ListItemIcon>
                {isExpanded && (
                  <>
                    <CustomListItemText primary='Administração' />
                    <span style={{ flex: 1 }} />
                    <span style={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 100 }}>
                      {adminOpen ? <ExpandLess /> : <ExpandMore />}
                    </span>
                  </>
                )}
              </CustomListItem>
              <Collapse in={adminOpen} timeout='auto' unmountOnExit>
                <List component='div' disablePadding>
                  {adminMenuItems.map(item => (
                    <Link key={item.id} to={item.url} style={TextDecorationNone}>
                      <Tooltip title={item.name} placement='right'>
                        <CustomListItem style={{ paddingLeft: 30 }} $active={pathname.startsWith(item.url)}>
                          <ListItemIcon style={ListIconStyle}>{item.icon}</ListItemIcon>
                          {isExpanded && <CustomListItemText primary={item.name} />}
                        </CustomListItem>
                      </Tooltip>
                    </Link>
                  ))}
                </List>
              </Collapse>
            </CustomList>
          )}
        </CustomBox>
      </Drawer>
    </>
  );
}

export default Sidebar;
